import { useMutation, useQueryClient } from "@tanstack/react-query";
import { merchantAppProductsKeys } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { merchantKeys } from "Api/Queries/Merchants/useGetMerchantQuery";
import { merchantsListKeys } from "Api/Queries/Merchants/useGetMerchantsQuery";
import { orbitLStatusKeys } from "Api/Queries/Merchants/useGetOrbitLStatusQuery";
import {
  type OrbitLTariff,
  type ProductCode,
  type TerminalFinancingModel,
  postMerchantsPublicIDAppProductsCode,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Parameters = {
  merchantPublicID: string;
  productCode: ProductCode;
  tariff?: OrbitLTariff | null;
  financingModel?: TerminalFinancingModel;
};

type Props = {
  onSuccess?: () => void;
  onError?: () => void;
};

export const useAddMerchantAppProductMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ merchantPublicID, productCode, tariff, financingModel }: Parameters) =>
        postMerchantsPublicIDAppProductsCode(
          { tariff, financingModel },
          merchantPublicID,
          productCode,
        ),
    ),
    onSuccess: (_, { merchantPublicID }: Parameters) => {
      queryClient.invalidateQueries({
        queryKey: merchantKeys.byMerchantPublicId(merchantPublicID),
      });
      queryClient.invalidateQueries({
        queryKey: merchantAppProductsKeys.byMerchantPublicId(merchantPublicID),
      });
      queryClient.invalidateQueries({
        queryKey: merchantsListKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: orbitLStatusKeys.byMerchantPublicId(merchantPublicID),
      });
      onSuccess?.();
    },
    onError,
  });
};
